import type { CustomPath, GroupTransform, PathCommand, Point, ShapeGeometry, Transform } from '@slideforge/shared';
import { IDENTITY_TRANSFORM } from '@slideforge/shared';
import type { Element } from '../xml.js';

export function parseTransform(xfrm: Element | undefined): Transform | undefined {
  if (!xfrm) return undefined;
  const off = xfrm.child('a:off');
  const ext = xfrm.child('a:ext');
  return {
    x: off?.int('x', 0) ?? 0,
    y: off?.int('y', 0) ?? 0,
    cx: ext?.int('cx', 0) ?? 0,
    cy: ext?.int('cy', 0) ?? 0,
    rot: xfrm.int('rot', 0),
    flipH: xfrm.bool('flipH', false),
    flipV: xfrm.bool('flipV', false),
  };
}

export function parseGroupTransform(xfrm: Element | undefined): GroupTransform {
  const base = parseTransform(xfrm) ?? IDENTITY_TRANSFORM;
  const chOff = xfrm?.child('a:chOff');
  const chExt = xfrm?.child('a:chExt');
  return {
    ...base,
    chX: chOff?.int('x', 0) ?? 0,
    chY: chOff?.int('y', 0) ?? 0,
    // A missing child extent means "same as the placed extent", i.e. no scaling.
    chCx: chExt?.int('cx', base.cx) ?? base.cx,
    chCy: chExt?.int('cy', base.cy) ?? base.cy,
  };
}

export function parseGeometry(spPr: Element | undefined): ShapeGeometry {
  if (!spPr) return { kind: 'none' };

  const prst = spPr.child('a:prstGeom');
  if (prst) {
    const adjustments: Record<string, number> = {};
    for (const gd of prst.path('a:avLst')?.all('a:gd') ?? []) {
      const name = gd.attr('name');
      const fmla = gd.attr('fmla');
      if (!name || !fmla) continue;
      // Adjust values are written as `val 12345`; anything else is a formula we
      // do not evaluate, and skipping it leaves the preset's own default.
      const match = /^val\s+(-?\d+)$/.exec(fmla);
      if (match?.[1]) adjustments[name] = Number.parseInt(match[1], 10);
    }
    return { kind: 'preset', preset: prst.attr('prst') ?? 'rect', adjustments };
  }

  const cust = spPr.child('a:custGeom');
  if (cust) {
    const paths: CustomPath[] = [];
    for (const path of cust.path('a:pathLst')?.all('a:path') ?? []) {
      paths.push({
        w: path.int('w', 0),
        h: path.int('h', 0),
        fill: (path.attr('fill') ?? 'norm') as CustomPath['fill'],
        stroke: path.bool('stroke', true),
        commands: parsePathCommands(path),
      });
    }
    return { kind: 'custom', paths };
  }

  return { kind: 'none' };
}

function parsePathCommands(path: Element): PathCommand[] {
  const out: PathCommand[] = [];
  for (const el of path.children) {
    switch (el.name) {
      case 'a:moveTo':
        pushPoints(out, 'moveTo', el);
        break;
      case 'a:lnTo':
        pushPoints(out, 'lnTo', el);
        break;
      case 'a:cubicBezTo':
        pushPoints(out, 'cubicBezTo', el);
        break;
      case 'a:quadBezTo':
        pushPoints(out, 'quadBezTo', el);
        break;
      case 'a:arcTo':
        out.push({
          op: 'arcTo',
          wR: el.int('wR', 0),
          hR: el.int('hR', 0),
          stAng: el.int('stAng', 0),
          swAng: el.int('swAng', 0),
        });
        break;
      case 'a:close':
        out.push({ op: 'close' });
        break;
      default:
        break;
    }
  }
  return out;
}

function pushPoints(
  out: PathCommand[],
  op: 'moveTo' | 'lnTo' | 'cubicBezTo' | 'quadBezTo',
  el: Element,
): void {
  const pts: Point[] = el.all('a:pt').map((pt) => ({ x: pt.int('x', 0), y: pt.int('y', 0) }));
  if (pts.length > 0) out.push({ op, pts } as PathCommand);
}
